"use client";

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Loader } from "lucide-react";
function OfferteForm({
  onSuccess
}: {
  onSuccess?: () => void;
}) {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  useEffect(() => {
    if (submitError) {
      console.error("Form submission error:", submitError);
    }
  }, [submitError]);
  useEffect(() => {
    if (isSubmitted && !submitError) {
      const timer = setTimeout(() => {
        resetForm();
        onSuccess?.();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isSubmitted, submitError, resetForm, onSuccess]);
  if (isSubmitted && !submitError) {
    return <div className="p-8 text-center bg-white rounded-lg">
        <div className="mb-4 flex justify-center">
          <div className="w-12 h-12 bg-[#1AAD4A]/10 rounded-full flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-[#1AAD4A] border-t-transparent rounded-full animate-pulse" />
          </div>
        </div>
        <p className="text-[#1AAD4A] font-bold text-xl mb-2">Aanvraag verzonden!</p>
        <p className="text-[#627881]">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <input type="hidden" name="_wpnonce" value="" />
      <FormField control={control} name="name" rules={{
      required: "Verplicht"
    }} render={({
      field
    }) => <FormItem>
            <FormControl>
              <Input placeholder="Naam" {...field} className="border-[#DCDFE0] rounded-[4px] h-12 focus-visible:ring-[#1AAD4A]/20" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Verplicht",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Ongeldig e-mailadres"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormControl>
              <Input type="email" placeholder="E-mail" {...field} className="border-[#DCDFE0] rounded-[4px] h-12 focus-visible:ring-[#1AAD4A]/20" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phoneNumber" rules={{
      required: "Verplicht"
    }} render={({
      field
    }) => <FormItem>
            <FormControl>
              <Input placeholder="Telefoonnummer" {...field} className="border-[#DCDFE0] rounded-[4px] h-12 focus-visible:ring-[#1AAD4A]/20" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="serviceType" rules={{
      required: "Maak een keuze"
    }} render={({
      field
    }) => <FormItem className="space-y-4">
            <FormLabel className="text-lg font-bold text-[#182329] block">Ik wens een:</FormLabel>
            <FormControl>
              <RadioGroup onValueChange={field.onChange} defaultValue={field.value} className="flex flex-col space-y-3">
                <div className="flex items-center space-x-3">
                  <RadioGroupItem value="asbestverwijdering" id="asbestverwijdering" className="border-[#DCDFE0] text-[#1AAD4A]" />
                  <Label htmlFor="asbestverwijdering" className="text-[#182329] font-normal cursor-pointer">asbestverwijdering</Label>
                </div>
                <div className="flex items-center space-x-3">
                  <RadioGroupItem value="asbestattest" id="asbestattest" className="border-[#DCDFE0] text-[#1AAD4A]" />
                  <Label htmlFor="asbestattest" className="text-[#182329] font-normal cursor-pointer">asbestattest</Label>
                </div>
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-2 gap-4">
        <FormField control={control} name="postcode" rules={{
        required: "Verplicht"
      }} render={({
        field
      }) => <FormItem>
              <FormControl>
                <Input placeholder="Postcode" {...field} className="border-[#DCDFE0] rounded-[4px] h-12 focus-visible:ring-[#1AAD4A]/20" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={control} name="city" rules={{
        required: "Verplicht"
      }} render={({
        field
      }) => <FormItem>
              <FormControl>
                <Input placeholder="Gemeente" {...field} className="border-[#DCDFE0] rounded-[4px] h-12 focus-visible:ring-[#1AAD4A]/20" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="street" rules={{
      required: "Verplicht"
    }} render={({
      field
    }) => <FormItem>
            <FormControl>
              <Input placeholder="Straat" {...field} className="border-[#DCDFE0] rounded-[4px] h-12 focus-visible:ring-[#1AAD4A]/20" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="houseNumber" rules={{
      required: "Verplicht"
    }} render={({
      field
    }) => <FormItem>
            <FormControl>
              <Input placeholder="Huisnummer" {...field} className="border-[#DCDFE0] rounded-[4px] h-12 focus-visible:ring-[#1AAD4A]/20" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <Button type="submit" disabled={isSubmitting} className="w-full bg-[#1AAD4A] hover:bg-[#158f3d] text-white font-bold h-14 rounded-[4px] text-lg transition-all shadow-none cursor-pointer">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Versturen...
          </> : "Ga"}
      </Button>

      {submitError && <div className="p-3 bg-red-50 text-red-600 text-sm rounded-[4px] border border-red-100">
          {submitError}
        </div>}
    </Form>;
}
export default function ReadyToFeast() {
  const [isOpen, setIsOpen] = useState(false);
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <section data-section-id="162206" id="ontvang-gratis-offertes" className="bg-white text-[#182329] relative py-32 md:py-48 lg:py-56 overflow-hidden flex items-center justify-center min-h-[60vh] border-y border-[#DCDFE0]">
      {}
      <div className="absolute inset-0 bg-transparent opacity-0" aria-hidden="true" />
      
      {}
      <div className="absolute inset-0 bg-white" />
      <div className="absolute inset-0 bg-white" />

      {}
      <div ref={ref} className={`relative z-10 container mx-auto px-4 text-center flex flex-col items-center justify-center gap-12 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <h2 className="text-[26px] font-bold font-serif tracking-normal text-[#182329] max-w-4xl mx-auto leading-tight">
          Ontvang gratis offertes en bespaar tot 40%
        </h2>

        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button size="lg" className="h-auto text-lg font-bold px-10 py-6 md:px-14 md:py-8 rounded-[4px] bg-[#1AAD4A] text-white shadow-none hover:bg-[#158f3d] transition-all duration-300 hover:-translate-y-2 border-none cursor-pointer">
              Vraag HIER uw offerte aan
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px] p-6 md:p-10 rounded-[4px]">
            <DialogHeader className="mb-8">
              <DialogTitle className="text-2xl font-bold font-serif text-[#182329] text-left">
                Ontvang gratis offertes
              </DialogTitle>
            </DialogHeader>
            <FormProvider formId="ready-to-feast-offerte" sectionName="ReadyToFeast" defaultValues={{
            serviceType: "asbestverwijdering",
            postcode: "",
            city: "",
            street: "",
            houseNumber: "",
            name: "",
            email: "",
            phoneNumber: ""
          }} successMessage="Bedankt voor uw aanvraag. Wij nemen zo snel mogelijk contact met u op.">
              <OfferteForm onSuccess={() => setIsOpen(false)} />
            </FormProvider>
          </DialogContent>
        </Dialog>
      </div>
    </section>;
}